import React from 'react'
import { Fieldset } from '@local/do-secundo-fieldset'
import * as CustomerInfo from './CustomerInfo/CustomerInfo'
import { CheckoutDeliveryInfo } from './CheckoutDeliveryInfo/CheckoutDeliveryInfo'
import { DynamicFormSection } from '../DynamicForms/DynamicFormSection'
import { PromoCodeInput } from './PromoCode/PromoCodeInput'
import { TaxExempt } from './TaxExempt/TaxExempt'
import { LoyaltyAccountLookup } from './LoyaltyAccountLookup'
import { CartCheckoutFields } from './CartCheckoutFields'
import { PaymentToggle } from './PaymentToggle'
import { MarketingCheckboxes } from './MarketingCheckboxes'
import { CheckoutError } from '../CheckoutError/CheckoutError'
import { CheckoutFormBodyProps } from './utils'
import { t } from '@local/translations'

interface CheckoutDetailsStepProps extends CheckoutFormBodyProps {
  creditCardSection?: React.ReactNode
}

/**
 * Renders the common checkout form UI shared by both domestic and international flows.
 * Displays all form sections (customer, delivery, payment, cart) with the action button passed by parent.
 * Used by both DomesticCheckoutFormBody and InternationalCheckoutFormBody.
 */
export const CheckoutDetailsStep = ({
  creditCardSection,
  customer,
  values,
  isSubmitting,
  confirmOrderError,
  setEditingAccount,
  checkoutProps,
  canShowTip
}: CheckoutDetailsStepProps) => {
  const {
    cart,
    eventType,
    restaurantInfo,
    showPaymentInformation,
    isPayLaterRequired,
    checkoutPaymentAmount
  } = checkoutProps
  const restaurantName = restaurantInfo?.name

  // Cart is guaranteed to exist when this component is rendered
  if (!cart) {
    return null
  }
  return (
    <>
      <CustomerInfo.CustomerInfo
        cart={cart}
        customer={customer}
        companyName={values.companyName}
        setEditingAccount={setEditingAccount}
      />
      <CheckoutDeliveryInfo />
      {eventType && (
        <Fieldset label={t('checkout-details-step.details')}>
          <DynamicFormSection formFields={eventType.formFields} />
        </Fieldset>
      )}
      <LoyaltyAccountLookup cart={cart} customer={customer} />
      {showPaymentInformation && (
        <Fieldset
          label={t('checkout-details-step.payment-information')}
          collapsable
          id='payment_type_fields'
        >
          {isPayLaterRequired && (
            <h3>
              {t('checkout-details-step.payment-follow-up', {
                restaurantName:
                  restaurantName || t('checkout-details-step.the-restaurant')
              })}
            </h3>
          )}
          {checkoutPaymentAmount.isPartialPaymentRequired && (
            <h3>
              {t('checkout-details-step.partial-payment-required', {
                percentage: checkoutPaymentAmount.partialPaymentPercentage
              })}
            </h3>
          )}
          <PromoCodeInput cart={cart} />
          <TaxExempt cart={cart} />
          <PaymentToggle checkoutProps={checkoutProps} />
          {creditCardSection}
        </Fieldset>
      )}
      <CartCheckoutFields
        values={values}
        customer={customer}
        cart={cart}
        showTip={canShowTip ? 'edit' : 'hide'}
        checkoutPaymentAmount={checkoutPaymentAmount}
      />
      <MarketingCheckboxes checkoutProps={checkoutProps} />
      {confirmOrderError && (
        <div className='px-5 pb-0'>
          <CheckoutError
            loading={isSubmitting}
            error={confirmOrderError.error}
            cart={cart}
          />
        </div>
      )}
    </>
  )
}
